import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type { TIssueComment, TIssueCommentReaction } from "@/types/issue-comment";

export class IssueCommentsStore {
  commentsByIssueId = observable.map<string, TIssueComment[]>();
  loadingByIssueId = observable.map<string, boolean>();
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  getComments(issueId: string): TIssueComment[] {
    return this.commentsByIssueId.get(issueId) ?? [];
  }

  async fetchComments(workspaceSlug: string, projectId: string, issueId: string): Promise<void> {
    this.loadingByIssueId.set(issueId, true);
    try {
      // A project-less work item (an app-managed event / Session) has no
      // project-scoped route — resolve its comments at workspace scope, or the
      // request lands on `/projects//…` and 404s (NG-717).
      const comments = projectId
        ? await this.rootStore.issueCommentService.listComments(workspaceSlug, projectId, issueId)
        : await this.rootStore.issueCommentService.listWorkspaceComments(workspaceSlug, issueId);
      runInAction(() => {
        this.commentsByIssueId.set(issueId, comments);
        this.loadingByIssueId.set(issueId, false);
      });
    } catch {
      runInAction(() => {
        this.loadingByIssueId.set(issueId, false);
      });
      throw new Error("Failed to fetch comments");
    }
  }

  async createComment(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    data: Partial<TIssueComment>,
    source: AnalyticsSource = "page",
  ): Promise<TIssueComment> {
    // Project-less work items (app-managed events) post at workspace scope; the
    // project-scoped route would resolve to `/projects//…` and 404 (NG-717).
    const comment = projectId
      ? await this.rootStore.issueCommentService.createComment(
          workspaceSlug,
          projectId,
          issueId,
          data,
        )
      : await this.rootStore.issueCommentService.createWorkspaceComment(
          workspaceSlug,
          issueId,
          data,
        );
    runInAction(() => {
      this.commentsByIssueId.set(issueId, [...this.getComments(issueId), comment]);
    });
    // The backend logs a "commented" activity row; revalidate an already-open
    // activity feed so the comment shows up there without a manual refresh. The
    // activity feed is project-scoped, so this is a no-op for a project-less item.
    if (projectId) {
      void this.rootStore.issueDetail.activity.refreshIfCached(workspaceSlug, projectId, issueId);
    }
    track("task_comment_posted", {
      task_id: issueId,
      project_id: projectId || null,
      comment_id: comment.id,
      source,
    });
    return comment;
  }

  async updateComment(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    commentId: string,
    data: Partial<TIssueComment>,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const comment = await this.rootStore.issueCommentService.updateComment(
      workspaceSlug,
      projectId,
      issueId,
      commentId,
      data,
    );
    runInAction(() => {
      this.commentsByIssueId.set(
        issueId,
        this.getComments(issueId).map((item) => (item.id === commentId ? comment : item)),
      );
    });
    // No UI edits a comment today; the store reports the write for when one does.
    track("task_comment_updated", {
      task_id: issueId,
      project_id: projectId,
      comment_id: commentId,
      source,
    });
    void this.rootStore.issueDetail.activity.refreshIfCached(workspaceSlug, projectId, issueId);
  }

  async deleteComment(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    commentId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.issueCommentService.deleteComment(
      workspaceSlug,
      projectId,
      issueId,
      commentId,
    );
    runInAction(() => {
      this.commentsByIssueId.set(
        issueId,
        this.getComments(issueId).filter((item) => item.id !== commentId),
      );
    });
    track("task_comment_deleted", {
      task_id: issueId,
      project_id: projectId,
      comment_id: commentId,
      source,
    });
    void this.rootStore.issueDetail.activity.refreshIfCached(workspaceSlug, projectId, issueId);
  }

  async addReaction(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    commentId: string,
    reaction: string,
    source: AnalyticsSource = "page",
  ): Promise<TIssueCommentReaction> {
    const result = await this.rootStore.issueCommentService.addReaction(
      workspaceSlug,
      projectId,
      commentId,
      { reaction },
    );
    runInAction(() => {
      this.commentsByIssueId.set(
        issueId,
        this.getComments(issueId).map((item) =>
          item.id === commentId
            ? { ...item, comment_reactions: [...(item.comment_reactions ?? []), result] }
            : item,
        ),
      );
    });
    track("comment_reaction_added", { task_id: issueId, comment_id: commentId, source });
    return result;
  }

  async removeReaction(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    commentId: string,
    reactionCode: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.issueCommentService.removeReaction(
      workspaceSlug,
      projectId,
      commentId,
      reactionCode,
    );
    runInAction(() => {
      this.commentsByIssueId.set(
        issueId,
        this.getComments(issueId).map((item) =>
          item.id === commentId
            ? {
                ...item,
                comment_reactions: (item.comment_reactions ?? []).filter(
                  (reaction) => reaction.reaction !== reactionCode,
                ),
              }
            : item,
        ),
      );
    });
    track("comment_reaction_removed", { task_id: issueId, comment_id: commentId, source });
  }
}
